import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { filesApi } from '@/lib/api';
import { formatFileSize } from '@/constants/utils/files';
import Dropzone from './Dropzone';
import { describeFileLimitViolation } from '@/constants/utils/file_upload_limits';

const TOOL_OPTIONS = [
  { key: 'brush', label: 'Brush',  icon: 'paintbrush' },
  { key: 'erase', label: 'Erase',  icon: 'eraser' },
  { key: 'rect',  label: 'Rect',   icon: 'square-dashed' },
];

const DEFAULT_BRUSH = 32;
const MIN_BRUSH = 4;
const MAX_BRUSH = 120;

/**
 * ImageMaskEditor — upload an image + paint a binary mask over it.
 *
 * On web, lays a real <canvas> over the source image and paints white strokes
 * where the user wants the model to operate. The canvas resolution matches
 * the source image's natural pixel dimensions so the emitted mask aligns
 * pixel-perfect with the original image. The mask is exported as a base64
 * PNG and stashed on `value.mask` as a data URL, so backends/tools can do
 * whatever they want with it (re-upload, decode, etc.) without a separate
 * dedicated endpoint.
 *
 * Tools:
 *   brush — round white stroke (smoothed by drawing line segments).
 *   erase — destination-out composite, removes mask under the cursor.
 *   rect  — drag-to-draw filled rectangle, committed on mouse-up.
 *
 * Native: same data flow but no painting layer (yet) — mirrors the legacy
 * placeholder and continues to upload the source image.
 */
function ImageMaskEditor({
  value,
  onChange,
  disabled,
  accept = 'image/*',
  label = 'Drop an image to mask',
  limitsHint,
  maxSizeBytes,
}) {
  const { colors } = useTheme();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTool, setActiveTool] = useState('brush');
  const [brushSize, setBrushSize] = useState(DEFAULT_BRUSH);
  const [pointerPos, setPointerPos] = useState(null); // { x, y } in CSS px relative to wrapper
  const [rectDraft, setRectDraft] = useState(null); // { x0, y0, x1, y1 } in canvas px

  const fileId = value && typeof value === 'object'
    ? (value.id || value._id || value.fileId || value.imageId)
    : (typeof value === 'string' ? value : null);
  const imageUrl = value?.cdnPath || value?.url || null;
  const existingMask = value?.mask || null;
  // Placeholder presets carry a cdnPath without a real backend file id.
  const isSample = !!(value && typeof value === 'object' && !fileId && imageUrl);

  // ---------------------------------------------------------------------------
  // Canvas wiring (web only). The canvas is sized to the source image's natural
  // pixels so the exported mask matches the source. CSS scales it to fit the
  // wrapper. We track:
  //   - canvasRef           the actual painting target
  //   - imgRef              the loaded HTMLImageElement, used to read natural
  //                         dimensions and to scale incoming pointer coords
  //   - drawingRef          { isDown, lastX, lastY } during a stroke
  // ---------------------------------------------------------------------------
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const wrapperRef = useRef(null);
  const drawingRef = useRef({ isDown: false, lastX: 0, lastY: 0, start: null });

  const styles = useMemo(() => StyleSheet.create({
    wrap: { gap: 12 },
    surface: {
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      borderRadius: 14,
      backgroundColor: colors.solid_color_tertiary,
      overflow: 'hidden',
    },
    canvasWrap: {
      width: '100%',
      aspectRatio: 16 / 9,
      backgroundColor: '#000',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
    },
    canvasImage: { width: '100%', height: '100%' },
    body: { padding: 14, gap: 12 },
    headerRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    iconBubble: {
      width: 36, height: 36, borderRadius: 999,
      alignItems: 'center', justifyContent: 'center',
      backgroundColor: colors.solid_color_block,
      borderWidth: 1, borderColor: colors.border_color_secondary,
    },
    headerMeta: { flex: 1, minWidth: 0, gap: 2 },
    removeBtn: {
      width: 32, height: 32, borderRadius: 999,
      alignItems: 'center', justifyContent: 'center',
      borderWidth: 1, borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
    },

    toolbar: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 8,
    },
    toolPill: {
      flexDirection: 'row', alignItems: 'center', gap: 6,
      paddingHorizontal: 10, paddingVertical: 7,
      borderRadius: 999,
      backgroundColor: colors.solid_color_block,
      borderWidth: 1, borderColor: colors.border_color_secondary,
    },
    toolPillActive: {
      backgroundColor: colors.solid_color_tertiary,
      borderColor: colors.text_color_primary,
    },
    spacer: { flex: 1 },
    clearBtn: {
      flexDirection: 'row', alignItems: 'center', gap: 6,
      paddingHorizontal: 10, paddingVertical: 7,
      borderRadius: 8,
      backgroundColor: colors.solid_color_block,
      borderWidth: 1, borderColor: colors.border_color_secondary,
    },

    sizeRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    sizeLabel: { minWidth: 60 },

    cursor: {
      position: 'absolute',
      borderRadius: 999,
      borderWidth: 2,
      borderColor: '#ffffff',
      ...(Platform.OS === 'web' && { boxShadow: '0 0 0 1px rgba(0,0,0,0.6)' }),
      pointerEvents: 'none',
    },

    hint: {
      position: 'absolute',
      bottom: 12, left: 12,
      flexDirection: 'row', alignItems: 'center', gap: 6,
      paddingHorizontal: 10, paddingVertical: 6,
      borderRadius: 999,
      backgroundColor: 'rgba(0,0,0,0.55)',
    },
  }), [colors]);

  // Convert a CSS-space pointer coord to canvas-space pixel coord.
  const toCanvasCoords = useCallback((evt) => {
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    if (!wrapper || !canvas) return null;
    const rect = wrapper.getBoundingClientRect();
    const cssX = evt.clientX - rect.left;
    const cssY = evt.clientY - rect.top;
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    return { cssX, cssY, x: cssX * sx, y: cssY * sy };
  }, []);

  // Persist current canvas to value.mask. We debounce-via-rAF so a long stroke
  // doesn't fire onChange on every move — only when the stroke pauses or ends.
  const persistMask = useCallback(() => {
    if (!canvasRef.current || !value || typeof value !== 'object') return;
    try {
      const mask = canvasRef.current.toDataURL('image/png');
      onChange?.({ ...value, mask });
    } catch {
      // toDataURL throws on cross-origin/canvas-tainted; we control the canvas
      // ourselves so this should never trip, but swallow defensively.
    }
  }, [onChange, value]);

  const persistRafRef = useRef(0);
  const schedulePersist = useCallback(() => {
    if (Platform.OS !== 'web') return;
    if (persistRafRef.current) cancelAnimationFrame(persistRafRef.current);
    persistRafRef.current = requestAnimationFrame(persistMask);
  }, [persistMask]);

  // ---------------------------------------------------------------------------
  // Image-loaded → size the canvas to natural dimensions and replay any
  // existing stored mask so users can keep editing across remounts.
  // ---------------------------------------------------------------------------
  const handleImgLoad = useCallback(() => {
    if (Platform.OS !== 'web') return;
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;
    const w = img.naturalWidth || 1024;
    const h = img.naturalHeight || 1024;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);

    if (existingMask && typeof existingMask === 'string') {
      const m = new window.Image();
      m.onload = () => { ctx.drawImage(m, 0, 0, w, h); };
      m.src = existingMask;
    }
  }, [existingMask]);

  // ---------------------------------------------------------------------------
  // Painting handlers. Brush + Erase share a stroke-segment path; Rect tracks
  // a draft and commits a filled rectangle on mouse-up.
  // ---------------------------------------------------------------------------
  const startStroke = useCallback((evt) => {
    if (disabled || Platform.OS !== 'web') return;
    const c = toCanvasCoords(evt);
    if (!c) return;
    drawingRef.current.isDown = true;
    drawingRef.current.lastX = c.x;
    drawingRef.current.lastY = c.y;
    drawingRef.current.start = { x: c.x, y: c.y };

    if (activeTool === 'rect') {
      setRectDraft({ x0: c.cssX, y0: c.cssY, x1: c.cssX, y1: c.cssY });
      return;
    }

    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = brushSize;
    if (activeTool === 'erase') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.fillStyle = 'rgba(0,0,0,1)';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = '#ffffff';
      ctx.fillStyle = '#ffffff';
    }
    // Dot at click point so single clicks always leave a mark.
    ctx.beginPath();
    ctx.arc(c.x, c.y, brushSize / 2, 0, Math.PI * 2);
    ctx.fill();
  }, [activeTool, brushSize, disabled, toCanvasCoords]);

  const moveStroke = useCallback((evt) => {
    if (Platform.OS !== 'web') return;
    const c = toCanvasCoords(evt);
    if (!c) return;
    setPointerPos({ x: c.cssX, y: c.cssY });

    if (!drawingRef.current.isDown) return;

    if (activeTool === 'rect') {
      setRectDraft((prev) => prev ? { ...prev, x1: c.cssX, y1: c.cssY } : prev);
      return;
    }

    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(drawingRef.current.lastX, drawingRef.current.lastY);
    ctx.lineTo(c.x, c.y);
    ctx.stroke();
    drawingRef.current.lastX = c.x;
    drawingRef.current.lastY = c.y;
  }, [activeTool, toCanvasCoords]);

  const endStroke = useCallback((evt) => {
    if (Platform.OS !== 'web') return;
    const wasDown = drawingRef.current.isDown;
    drawingRef.current.isDown = false;
    if (!wasDown) return;

    if (activeTool === 'rect') {
      const c = toCanvasCoords(evt);
      const start = drawingRef.current.start;
      if (start && c) {
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) {
          ctx.globalCompositeOperation = 'source-over';
          ctx.fillStyle = '#ffffff';
          const x = Math.min(start.x, c.x);
          const y = Math.min(start.y, c.y);
          const w = Math.abs(c.x - start.x);
          const h = Math.abs(c.y - start.y);
          if (w > 0 && h > 0) ctx.fillRect(x, y, w, h);
        }
      }
      setRectDraft(null);
    }
    schedulePersist();
  }, [activeTool, schedulePersist, toCanvasCoords]);

  // Pointer-leave clears the cursor so the brush ring doesn't get stuck.
  const handlePointerLeave = useCallback(() => {
    setPointerPos(null);
    if (drawingRef.current.isDown) {
      drawingRef.current.isDown = false;
      schedulePersist();
    }
  }, [schedulePersist]);

  const handleClearMask = useCallback(() => {
    if (Platform.OS !== 'web') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    persistMask();
  }, [persistMask]);

  useEffect(() => () => {
    if (persistRafRef.current) cancelAnimationFrame(persistRafRef.current);
  }, []);

  // ---------------------------------------------------------------------------
  // Upload / library / clear
  // ---------------------------------------------------------------------------
  const uploadFile = useCallback(async (files) => {
    const file = Array.isArray(files) ? files[0] : files;
    if (!file) return;
    setError(null);
    const limitError = describeFileLimitViolation(file, { maxSizeBytes });
    if (limitError) {
      setError(limitError);
      return;
    }
    setIsUploading(true);
    try {
      const { file: saved } = await filesApi.uploadFile({ file });
      if (!saved?.id) throw new Error('Upload failed');
      onChange?.({
        id: saved.id,
        imageId: saved.id,
        name: saved.name || saved.originalName || file.name,
        mimeType: saved.mimeType || file.type,
        sizeInBytes: saved.sizeInBytes ?? saved.sizeBytes ?? file.size,
        cdnPath: saved.cdnPath || null,
        thumbnailPath: saved.thumbnailPath || null,
        fileType: saved.fileType,
        mask: null,
      });
    } catch (e) {
      setError(e?.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  }, [onChange, maxSizeBytes]);

  const handleLibrary = useCallback((picked) => {
    const saved = picked?.[0];
    if (!saved?.id) return;
    const limitError = describeFileLimitViolation(saved, { maxSizeBytes });
    if (limitError) {
      setError(limitError);
      return;
    }
    setError(null);
    onChange?.({
      id: saved.id,
      imageId: saved.id,
      name: saved.name || saved.originalName,
      mimeType: saved.mimeType,
      sizeInBytes: saved.sizeInBytes ?? saved.sizeBytes,
      cdnPath: saved.cdnPath || null,
      thumbnailPath: saved.thumbnailPath || null,
      fileType: saved.fileType,
      mask: null,
    });
  }, [onChange, maxSizeBytes]);

  const clearFile = useCallback(() => onChange?.(null), [onChange]);

  if (!fileId && !isSample) {
    return (
      <View style={styles.wrap}>
        <Dropzone
          accept={accept}
          disabled={disabled}
          isUploading={isUploading}
          icon='paintbrush'
          primaryLabel={label}
          secondaryLabel={limitsHint ? `PNG, JPG, WebP · ${limitsHint}` : 'PNG, JPG, WebP'}
          onFiles={uploadFile}
          onLibrary={handleLibrary}
        />
        {!!error && <Text size='s' color='danger'>{error}</Text>}
      </View>
    );
  }

  return (
    <View style={styles.wrap}>
      <View style={styles.surface}>
        {/*
          Web painting surface: an <img> behind a transparent <canvas>. The
          canvas captures pointer events (`touchAction: none` keeps page
          scroll from hijacking finger drags).
        */}
        {Platform.OS === 'web' ? (
          <div
            ref={wrapperRef}
            onMouseDown={startStroke}
            onMouseMove={moveStroke}
            onMouseUp={endStroke}
            onMouseLeave={handlePointerLeave}
            onTouchStart={(e) => { if (e.touches[0]) startStroke(e.touches[0]); }}
            onTouchMove={(e) => { if (e.touches[0]) moveStroke(e.touches[0]); }}
            onTouchEnd={(e) => endStroke(e.changedTouches?.[0] || e.touches?.[0] || { clientX: 0, clientY: 0 })}
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '16 / 9',
              backgroundColor: '#000',
              overflow: 'hidden',
              cursor: disabled ? 'not-allowed' : 'crosshair',
              touchAction: 'none',
              userSelect: 'none',
            }}
          >
            {imageUrl ? (
              // eslint-disable-next-line react/forbid-elements
              <img
                ref={imgRef}
                src={imageUrl}
                onLoad={handleImgLoad}
                draggable={false}
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  objectFit: 'contain',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              />
            ) : null}
            {/* eslint-disable-next-line react/forbid-elements */}
            <canvas
              ref={canvasRef}
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                opacity: 0.55,
                pointerEvents: 'none',
              }}
            />
            {/* Live brush-size cursor ring. Hidden when not over the surface
                or when the rect tool is active. */}
            {pointerPos && activeTool !== 'rect' && (
              <View
                pointerEvents='none'
                style={[
                  styles.cursor,
                  {
                    width: brushSize,
                    height: brushSize,
                    left: pointerPos.x - brushSize / 2,
                    top: pointerPos.y - brushSize / 2,
                  },
                ]}
              />
            )}
            {/* Rect-draft preview while dragging. */}
            {rectDraft && (
              <View
                pointerEvents='none'
                style={{
                  position: 'absolute',
                  left: Math.min(rectDraft.x0, rectDraft.x1),
                  top: Math.min(rectDraft.y0, rectDraft.y1),
                  width: Math.abs(rectDraft.x1 - rectDraft.x0),
                  height: Math.abs(rectDraft.y1 - rectDraft.y0),
                  backgroundColor: 'rgba(255,255,255,0.35)',
                  borderWidth: 1,
                  borderColor: '#ffffff',
                }}
              />
            )}
            <View style={styles.hint}>
              <Icon name={activeTool === 'erase' ? 'eraser' : (activeTool === 'rect' ? 'square-dashed' : 'paintbrush')} size={11} color='#ffffff' />
              <Text size='xs' weight='medium' style={{ color: '#ffffff' }}>
                {activeTool === 'rect' ? 'Drag to draw a rectangle' : (activeTool === 'erase' ? 'Drag to erase mask' : 'Drag to paint mask')}
              </Text>
            </View>
          </div>
        ) : (
          <View style={styles.canvasWrap}>
            {imageUrl ? (
              <Image source={{ uri: imageUrl }} style={styles.canvasImage} contentFit='contain' />
            ) : (
              <Icon name='image' size={36} color='#ffffff66' />
            )}
          </View>
        )}

        <View style={styles.body}>
          <View style={styles.headerRow}>
            <View style={styles.iconBubble}>
              <Icon name='image' size={14} color={colors.text_color_secondary} />
            </View>
            <View style={styles.headerMeta}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <Text size='s' weight='bold' numberOfLines={1} style={{ flexShrink: 1 }}>
                  {value?.name || (isSample ? 'Sample image' : 'Source image')}
                </Text>
                {isSample && (
                  <View style={{
                    flexDirection: 'row', alignItems: 'center', gap: 3,
                    paddingHorizontal: 6, paddingVertical: 2, borderRadius: 999,
                    backgroundColor: colors.solid_color_block,
                    borderWidth: 1, borderColor: colors.border_color_secondary,
                  }}>
                    <Icon name='sparkles' size={8} color={colors.text_color_secondary} />
                    <Text size='xxs' weight='bold' color='secondary'>SAMPLE</Text>
                  </View>
                )}
              </View>
              <Text size='xs' color='tertiary' numberOfLines={1}>
                {value?.sizeInBytes ? formatFileSize(value.sizeInBytes) : 'Image file'}
              </Text>
            </View>
            {!disabled && (
              <Pressable onPress={clearFile} style={styles.removeBtn} hitSlop={6}>
                <Icon name='xmark' size={12} color={colors.text_color_secondary} />
              </Pressable>
            )}
          </View>

          <View style={styles.toolbar}>
            {TOOL_OPTIONS.map(tool => {
              const isActive = activeTool === tool.key;
              return (
                <Pressable
                  key={tool.key}
                  onPress={() => setActiveTool(tool.key)}
                  style={[styles.toolPill, isActive && styles.toolPillActive]}
                >
                  <Icon
                    name={tool.icon}
                    size={11}
                    color={isActive ? colors.text_color_primary : colors.text_color_secondary}
                  />
                  <Text size='xs' weight='medium' color={isActive ? 'primary' : 'secondary'}>
                    {tool.label}
                  </Text>
                </Pressable>
              );
            })}

            <View style={styles.spacer} />

            {Platform.OS === 'web' && existingMask ? (
              <Pressable onPress={handleClearMask} style={styles.clearBtn}>
                <Icon name='trash' size={11} color={colors.text_color_secondary} />
                <Text size='xs' weight='medium' color='secondary'>Clear mask</Text>
              </Pressable>
            ) : null}
          </View>

          {Platform.OS === 'web' && activeTool !== 'rect' && (
            <View style={styles.sizeRow}>
              <Text size='xs' color='secondary' style={styles.sizeLabel}>Brush size</Text>
              {/* eslint-disable-next-line react/forbid-elements */}
              <input
                type='range'
                min={MIN_BRUSH}
                max={MAX_BRUSH}
                step={1}
                value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                style={{ flex: 1 }}
              />
              <Text size='xs' color='tertiary'>{brushSize}px</Text>
            </View>
          )}
        </View>
      </View>

      {!!error && <Text size='s' color='danger'>{error}</Text>}
    </View>
  );
}

export default React.memo(ImageMaskEditor);
